import { TranslationMap } from '@trimble-gcs/i18n-types';

export function importDialogTranslations() {
  return {
    title: {
      text: $localize`:@@import.dialog.title:Import Files`,
      desc: 'Title for the file import dialog.',
    },
    minimize: {
      text: $localize`:@@import.dialog.minimize:Minimize`,
      desc: 'Tooltip for the minimize button that hides the file import dialog.',
    },
    close: {
      tooltip: {
        text: $localize`:@@import.dialog.close.tooltip:Close`,
        desc: 'Tooltip for the top right "X" button that closes the file import dialog.',
      },
      button: {
        text: $localize`:@@import.dialog.close.button:Close`,
        desc: 'Button text to close the dialog once the import is complete..',
      },
    },
    fileError: {
      checkQuota: {
        text: $localize`:@@import.dialog.fileError.checkQuota:Error checking quota`,
        desc: 'Error message displayed when there is an error checking the quota for the file.',
      },
      quotaExceeded: {
        text: $localize`:@@import.dialog.fileError.quotaExceeded:Quota will be exceeded`,
        desc: 'Error message displayed when the file will exceed the available quota.',
      },
      createPointcloud: {
        text: $localize`:@@import.dialog.fileError.createPointcloud:Error creating pointcloud`,
        desc: 'Error message displayed when there is an error creating the pointcloud for the file.',
      },
      createImport: {
        text: $localize`:@@import.dialog.fileError.createImport:Error creating import`,
        desc: 'Error message displayed when there is an error creating the import for the file.',
      },
      createFiles: {
        text: $localize`:@@import.dialog.fileError.createFiles:Error allocating space`,
        desc: 'Error message displayed when there is an error allocating space for the file.',
      },
      uploadFiles: {
        text: $localize`:@@import.dialog.fileError.uploadFiles:Error during upload`,
        desc: 'Error message displayed when there is an error uploading the file.',
      },
      ingest: {
        text: $localize`:@@import.dialog.fileError.ingest:Error starting ingestion`,
        desc: 'Error message displayed when there is an error starting the ingestion process for the file.',
      },
    },
    fileStatus: {
      checkQuota: {
        text: $localize`:@@import.dialog.fileStatus.checkQuota:Checking quota`,
        desc: 'File status message displayed when the file is being checked for quota.',
      },
      createPointcloud: {
        text: $localize`:@@import.dialog.fileStatus.createPointcloud:Creating pointcloud`,
        desc: 'File status message displayed when the file is being created as a pointcloud.',
      },
      createImport: {
        text: $localize`:@@import.dialog.fileStatus.createImport:Creating import`,
        desc: 'File status message displayed when the file is being created as an import.',
      },
      createFiles: {
        text: $localize`:@@import.dialog.fileStatus.createFiles:Allocating space`,
        desc: 'File status message displayed when the file is being allocated space.',
      },
      uploadFiles: {
        text: $localize`:@@import.dialog.fileStatus.uploadFiles:Uploading`,
        desc: 'File status message displayed when the file is being uploaded.',
      },
      ingest: {
        text: $localize`:@@import.dialog.fileStatus.ingest:Starting ingestion`,
        desc: 'File status message displayed when the file is starting the ingestion process.',
      },
    },
    status: {
      selectionError: {
        text: $localize`:@@import.dialog.status.selectionError:Remove the file(s) with errors.`,
        desc: 'Message displayed when there are file(s) that must be removed from the user selection, because the file(s) cannot be imported.',
      },
      fileAlreadyImportedError: {
        text: $localize`:@@import.dialog.status.fileAlreadyImportedError:File already ingested.`,
        desc: 'Message displayed for a file that has already been imported.',
      },
      maxFilesWarning: {
        text: $localize`:@@import.dialog.status.maxFilesWarning:Reached the maximum of {maxFiles} files allowed per import.`,
        desc: 'Message displayed when the user has selected the maximum allowed files to import.',
      },
      inProgress: {
        title: {
          text: $localize`:@@import.dialog.status.inProgress.title:Import in Progress`,
          desc: 'Title displayed when the file import is in progress.',
        },
        message: {
          text: $localize`:@@import.dialog.status.inProgress.message:Do not close this browser tab while import is in progress.`,
          desc: 'Message displayed when the file import is in progress. The message is a warning to the user not to close the browser tab.',
        },
      },
      error: {
        text: $localize`:@@import.dialog.status.error:There was an error during import.`,
        desc: 'Message displayed when there was an error with the file import.',
      },
      complete: {
        text: $localize`:@@import.dialog.status.complete:Import completed.`,
        desc: 'Message displayed when the file import has completed.',
      },
    },
    importFrom: {
      tooltip: {
        text: $localize`:@@import.dialog.importFrom.tooltip:Import file/s from {source}`,
        desc: 'Tooltip on icon for each import source to choose from. Examples: Google drive, dropbox, oneDrive.',
      },
    },
    combine: {
      text: $localize`:@@import.dialog.combine:Combine files into a single package`,
      desc: 'Toggle button text to combine files into a single package. When on a single package containing all files will be created. When off a package per file will be created.',
    },
    classify: {
      text: $localize`:@@import.dialog.classify:Run classifier on import`,
      desc: "Toggle button text to classify the scan. When 'on' the user can select a classifier for the scan. When off no classifier will be applied.",
    },
    experimental: {
      tooltip: {
        text: $localize`:@@import.dialog.experimental.tooltip:Experimental Feature`,
        desc: 'Tooltip for the experimental feature icon in the import dialog.',
      },
    },
    cancel: {
      button: {
        text: $localize`:@@import.dialog.cancel.button:Cancel`,
        desc: 'Button text to cancel or abort the file import.',
      },
      confirm: {
        title: {
          text: $localize`:@@import.dialog.cancel.confirm.title:Cancel Import`,
          desc: 'Title for the confirmation dialog when the user tries to cancel the file import.',
        },
        message: {
          text: $localize`:@@import.dialog.cancel.confirm.message:Are you sure you want to cancel this import?`,
          desc: 'Message for the confirmation dialog when the user tries to cancel the file import.',
        },
        yes: {
          text: $localize`:@@import.dialog.cancel.confirm.yes:Yes`,
          desc: 'Button text to confirm the cancellation of the file import.',
        },
        no: {
          text: $localize`:@@import.dialog.cancel.confirm.no:No`,
          desc: 'Button text to cancel the cancellation of the file import.',
        },
      },
    },
    start: {
      text: $localize`:@@import.dialog.start:Import`,
      desc: 'Button text to start the file import.',
    },
  } satisfies TranslationMap;
}
